// Who acted, as a small avatar: AI for the system's own work, the accounting
// package's initials when the package answered (a confirmation, a sync), and a
// person's initials from the name the office set — or their address until then.
// Never a face or a name this app made up.

import type { OfficeMember } from '../api/types.ts';

export interface ActorLike {
  readonly administrationId: string | null;
  readonly actorKind?: string | undefined;
  readonly actorUserId?: string | null | undefined;
  readonly action: string;
}

/** The avatar for whoever acted: AI, the accounting package, or a person. */
export function actorAvatar(
  event: ActorLike,
  people: ReadonlyMap<string, OfficeMember>,
  providers: ReadonlyMap<string, string | null>,
): { initials: string; tone: string; name: string } {
  if (event.actorKind === 'system' || event.actorKind === undefined) {
    // A confirmation or a sync is the package answering; everything else the
    // system recorded is the AI's own work.
    if (event.action.startsWith('operation.confirmed') || event.action.startsWith('connection.sync')) {
      const provider = (event.administrationId === null ? null : providers.get(event.administrationId)) ?? null;
      const package_ = provider === null ? null : (PROVIDER_NAMES[provider] ?? provider);
      if (package_ !== null) return { initials: initialsOf(package_), tone: 'sy', name: package_ };
    }
    return { initials: 'AI', tone: 'ag', name: 'Your AI' };
  }
  const person = event.actorUserId == null ? undefined : people.get(event.actorUserId);
  const name = person?.displayName ?? person?.email ?? 'A colleague';
  return { initials: initialsOf(person?.displayName ?? person?.email?.split('@')[0] ?? '?'), tone: 'pp', name };
}

export const PROVIDER_NAMES: Readonly<Record<string, string>> = { eboekhouden: 'e-Boekhouden' };

export function initialsOf(name: string): string {
  if (name === 'e-Boekhouden') return 'EB';
  const words = name.replace(/[^\p{L}\s-]/gu, ' ').trim().split(/[\s-]+/).filter(Boolean);
  const letters = words.length > 1 ? `${words[0]?.[0] ?? ''}${words[words.length - 1]?.[0] ?? ''}` : (words[0] ?? '?').slice(0, 2);
  return letters.toUpperCase();
}

